export interface CementPlant {
  id: number;
  name: string;
  activa: boolean;
}

/** Cobertura territorial del centro (maestro ``CementoDatoGeografico``). */
export interface CementoCentroDatoGeografico {
  id: number;
  name: string;
  code: string;
}

export interface CementCentro {
  id: number;
  code: string;
  name: string;
  address_withdrawal: string;
  puesto: number | null;
  plant: CementPlant | null;
  active: boolean;
  datos_geograficos?: CementoCentroDatoGeografico[];
}

/** Respuesta API: ``nombre_publicacion`` anidado con tipo de despacho incoterm. */
export interface CementNombrePublicacionNested {
  texto: string;
  tipo_despacho: string;
}

/** Celda de política (grilla) para un incoterm: pallet 1..16 y horas reguladas. */
export interface CementoPoliticaPallet {
  pallet: number;
  horas: number;
}

export interface CementProductDeliverySummaryItem {
  incoterm: string;
  label: string;
  short_label: string;
  available: boolean;
  tipo_entrega: 'despacho' | 'retira_cliente';
}

export interface CementProductDeliverySummary {
  types: CementProductDeliverySummaryItem[];
}

export interface CementProductDeliveryOption {
  id: number;
  producto: string;
  nombre_publicacion?: string | CementNombrePublicacionNested;
  unidad?: string;
  incoterms?: string;
  incoterm_nombre_publicacion?: string;
  incoterm_detalle?: string;
  multiplo?: number | null;
  sku_pos10?: string;
  sku_pos20?: string;
  sku_pos30?: string;
  tipo_camion?: string;
  precio_bruto: string | null;
  display_label: string;
  centro: CementCentro | null;
  active: boolean;
  has_coverage?: boolean | null;
  politica_pallets?: CementoPoliticaPallet[] | null;
}

export interface CementProduct {
  id: number;
  producto: string;
  /** URL de imagen del producto; si no llega del backend se usa imagen genérica. */
  image_url?: string;
  nombre_publicacion?: string | CementNombrePublicacionNested;
  /** Etiqueta para selectores (texto + tipo); solo UI. */
  nombre_publicacion_label?: string;
  unidad?: string;
  incoterms?: string;
  incoterm_nombre_publicacion?: string;
  incoterm_detalle?: string;
  multiplo?: number | null;
  sku_pos10?: string;
  sku_pos20?: string;
  sku_pos30?: string;
  tipo_camion?: string;
  precio_bruto: string | null;
  display_label: string;
  centro: CementCentro | null;
  active: boolean;
  /** Cobertura de política para ``incoterms`` en la región/comuna del request; ausente si no hubo geo válida. */
  has_coverage?: boolean | null;
  /** Celdas pallet/horas para el incoterm del producto; ausente si no hubo geo válida. */
  politica_pallets?: CementoPoliticaPallet[] | null;
  /** Variantes de entrega por incoterm cuando el backend responde agrupado. */
  delivery_options?: CementProductDeliveryOption[];
  /** Resumen visual/operativo de variantes agrupadas. */
  delivery_summary?: CementProductDeliverySummary;
  /** Chips compactos para render en el selector. */
  delivery_badges?: Array<{ label: string; available: boolean }>;
  /** Incoterm activo elegido por frontend sobre ``delivery_options``. */
  selected_incoterm?: string;
}

export function cementProductNormalizeIncoterm(value: string | undefined | null): string {
  return String(value ?? '').trim().toUpperCase();
}

export function cementProductNombrePublicacionNested(
  product: CementProduct
): CementNombrePublicacionNested | null {
  const np = product.nombre_publicacion;
  if (
    !np ||
    typeof np !== 'object' ||
    !('texto' in np) ||
    !('tipo_despacho' in np)
  ) {
    return null;
  }
  return {
    texto: typeof np.texto === 'string' ? np.texto : '',
    tipo_despacho: typeof np.tipo_despacho === 'string' ? np.tipo_despacho : '',
  };
}

export function cementProductTipoDespachoLabel(product: CementProduct): string {
  const source = cementProductActiveDeliveryOption(product);
  const nested = cementProductNombrePublicacionNested(source);
  const fromNested = nested?.tipo_despacho?.trim();
  if (fromNested) {
    return fromNested;
  }
  return (source.incoterm_nombre_publicacion ?? '').trim();
}

export function cementProductNombrePublicacionTexto(product: CementProduct): string {
  const source = cementProductActiveDeliveryOption(product);
  const nested = cementProductNombrePublicacionNested(source);
  if (nested) {
    return (nested.texto ?? '').trim();
  }
  return typeof source.nombre_publicacion === 'string'
    ? source.nombre_publicacion.trim()
    : '';
}

export function cementProductNombrePublicacionSelectLabel(product: CementProduct): string {
  return cementProductNombrePublicacionTexto(product) || product.display_label;
}

export function cementProductActiveDeliveryOption(
  product: CementProduct
): CementProductDeliveryOption {
  const options = product.delivery_options ?? [];
  if (options.length === 0) {
    return product;
  }

  const selectedIncoterm = cementProductNormalizeIncoterm(product.selected_incoterm);
  if (selectedIncoterm) {
    const bySelected = options.find(
      (option) => cementProductNormalizeIncoterm(option.incoterms) === selectedIncoterm
    );
    if (bySelected) {
      return bySelected;
    }
  }

  const fallbackIncoterm = cementProductNormalizeIncoterm(product.incoterms);
  if (fallbackIncoterm) {
    const byTopLevel = options.find(
      (option) => cementProductNormalizeIncoterm(option.incoterms) === fallbackIncoterm
    );
    if (byTopLevel) {
      return byTopLevel;
    }
  }

  return options[0];
}

export function cementProductDeliverySummaryItems(
  product: CementProduct
): CementProductDeliverySummaryItem[] {
  const summaryItems = product.delivery_summary?.types ?? [];
  if (summaryItems.length > 0) {
    return summaryItems;
  }

  const seen = new Set<string>();
  return (product.delivery_options ?? [])
    .map((option) => {
      const incoterm = cementProductNormalizeIncoterm(option.incoterms);
      if (!incoterm || seen.has(incoterm)) {
        return null;
      }
      seen.add(incoterm);
      const label = cementProductDeliveryOptionLabel(option);
      return {
        incoterm,
        label,
        short_label: cementProductDeliveryOptionShortLabel(option),
        available: option.has_coverage !== false,
        tipo_entrega: incoterm === 'ZF5' ? 'retira_cliente' : 'despacho',
      } satisfies CementProductDeliverySummaryItem;
    })
    .filter((item): item is CementProductDeliverySummaryItem => item !== null);
}

export function cementProductDeliveryOptionLabel(option: CementProductDeliveryOption): string {
  const np = option.nombre_publicacion;
  if (np && typeof np === 'object' && 'tipo_despacho' in np) {
    return String(np.tipo_despacho ?? '').trim();
  }
  return (option.incoterm_nombre_publicacion ?? '').trim();
}

export function cementProductDeliveryOptionShortLabel(option: CementProductDeliveryOption): string {
  const label = cementProductDeliveryOptionLabel(option).toLowerCase();
  if (label.includes('piso')) {
    return 'A Piso';
  }
  if (label.includes('camion')) {
    return 'Sobre camión';
  }
  if (label.includes('retira')) {
    return 'Retiro';
  }
  return cementProductDeliveryOptionLabel(option) || cementProductNormalizeIncoterm(option.incoterms);
}

export function cementProductPrice(option: CementProduct | CementProductDeliveryOption | undefined | null): number {
  const raw = String(option?.precio_bruto ?? '').trim();
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : 0;
}

const CEMENTO_IVA_RATE = 0.19;

export function cementProductPriceWithIva(
  option: CementProduct | CementProductDeliveryOption | undefined | null
): number {
  const basePrice = cementProductPrice(option);
  if (basePrice <= 0) {
    return 0;
  }
  return Number((basePrice * (1 + CEMENTO_IVA_RATE)).toFixed(2));
}
